// Privacy Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "who-we-are", title: "Who we are",
    body: (<>
      <p>CareerOS-AI (&quot;CareerOS&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates the CareerOS-AI website, mobile apps, and AI-powered career tools available at <a href="https://www.careeros-ai.com">careeros-ai.com</a>.</p>
      <p>CareerOS-AI is headquartered in Ontario, Canada. This policy is written to reflect Canadian federal privacy law (PIPEDA) and Ontario privacy principles, and to be transparent to users in every country we serve.</p>
    </>) },
  { n: 2, id: "what-we-collect", title: "What we collect",
    body: (<>
      <p>We collect only what we need to provide, secure, and improve the Service.</p>
      <p><strong>Account &amp; profile data</strong> — email, name, password (hashed), date of birth (optional), country, region/state, city, timezone, preferred currency, career stage, industry, and any information you enter into your profile, resume, or goals.</p>
      <p><strong>Content you create</strong> — resumes, cover letters, chat messages with our AI, interview transcripts, financial-plan inputs, portfolios, and any documents you upload.</p>
      <p><strong>Usage &amp; technical data</strong> — IP address, browser type and version, device type, referrer, timestamps, and interactions with features (used to detect abuse, debug, and improve product decisions).</p>
      <p><strong>Payment data</strong> — handled by our payment processor (Stripe). We do not store full card numbers on our servers; we retain only a payment reference, plan, and billing status.</p>
      <p><strong>Cookies &amp; local storage</strong> — see our <Link to="/cookies">Cookie Policy</Link>.</p>
    </>) },
  { n: 3, id: "how-we-use-it", title: "How we use your data",
    body: (<>
      {bullets([
        "Deliver the Service — generate AI advice, benchmark salaries, match jobs, produce roadmaps, run financial simulations, and personalize your experience.",
        "Localize responses — every AI prompt uses your country, region, currency, and timezone so guidance is locally accurate (see our AI Usage Policy).",
        "Communicate with you — transactional emails (password resets, receipts, security alerts) and, where you opted in, product updates.",
        "Bill and manage subscriptions.",
        "Protect the Service — detect abuse, fraud, spam, brute-force attempts, and prevent account takeovers.",
        "Comply with legal obligations and enforce our Terms.",
      ])}
      <p>We do not sell your personal information.</p>
    </>) },
  { n: 4, id: "legal-bases", title: "Legal bases (users in the EU / UK / applicable regions)",
    body: (<>
      <p>Where the GDPR or UK GDPR applies, we process your data on the following legal bases:</p>
      {bullets([
        "Contract — to provide the Service you signed up for.",
        "Legitimate interests — to secure the Service, prevent abuse, and improve the product.",
        "Consent — for optional communications, non-essential cookies, and any use we ask you to opt into.",
        "Legal obligation — where we must retain or disclose data to comply with law.",
      ])}
    </>) },
  { n: 5, id: "ai-processing", title: "AI processing & third-party model providers",
    body: (<>
      <p>To generate AI responses we send prompts and profile context to third-party model providers such as Anthropic (Claude), OpenAI (GPT), and Google (Gemini). These providers process the data strictly to produce the response you requested.</p>
      <p>Our provider agreements specifically prohibit them from using your prompts or CareerOS content to train their public models. Where a provider offers zero-data-retention or short-retention API tiers, we use them.</p>
      <p>See our <Link to="/ai-policy">AI Usage &amp; Responsible AI Policy</Link> for full details.</p>
    </>) },
  { n: 6, id: "sharing", title: "Sharing your data",
    body: (<>
      <p>We share data only with the categories of recipients required to operate CareerOS:</p>
      {bullets([
        "Cloud infrastructure providers (hosting, databases, CDN).",
        "AI model providers (Anthropic, OpenAI, Google, and equivalents) — strictly to fulfil your requests.",
        "Email delivery (Resend and similar).",
        "Payment processing (Stripe).",
        "Analytics and error monitoring — configured to avoid capturing your resume or chat content.",
        "Regulators and law-enforcement — where legally compelled.",
        "Corporate transactions — in the event of a merger, acquisition, or asset sale, subject to equivalent privacy protections.",
      ])}
      <p>We do not share your personal data with advertisers.</p>
    </>) },
  { n: 7, id: "international-transfers", title: "International data transfers",
    body: (<>
      <p>Because CareerOS-AI is cloud-based and integrates with global providers, your data may be processed in Canada, the United States, and other countries in which our vendors operate.</p>
      <p>Where required by law we rely on Standard Contractual Clauses, adequacy decisions, or equivalent safeguards to protect data in transit.</p>
    </>) },
  { n: 8, id: "retention", title: "Data retention",
    body: (<>
      <p>We keep your data only as long as we need it. See our <Link to="/data-retention">Data Retention Policy</Link> for exact periods per data category.</p>
      <p>You can delete your account at any time from <Link to="/app/settings">Settings</Link>, which removes your profile and content from active systems.</p>
    </>) },
  { n: 9, id: "your-rights", title: "Your rights",
    body: (<>
      <p>Depending on your jurisdiction you may have some or all of the following rights:</p>
      {bullets([
        "Access — request a copy of the data we hold about you.",
        "Correction — fix inaccurate data.",
        "Deletion — request deletion of your account and data.",
        "Portability — receive your data in a machine-readable format.",
        "Objection or restriction — object to certain uses of your data.",
        "Withdraw consent — for anything you consented to.",
        "Complaint — lodge a complaint with your local data-protection authority (in Canada, the Office of the Privacy Commissioner; in the EU, your national DPA; in the UK, the ICO; in California, the CPPA).",
      ])}
      <p>To exercise any right, use the controls in <Link to="/app/settings">Settings</Link> or email <span className="font-mono">support@careeros-ai.com</span>. We respond within 30 days.</p>
    </>) },
  { n: 10, id: "security", title: "Security",
    body: (<>
      <p>We take reasonable and appropriate measures to protect your data: TLS in transit, encryption of sensitive fields at rest, hashed passwords (bcrypt), least-privilege access controls, rate limiting, and audit logging. See our <Link to="/security">Security Policy</Link>.</p>
      <p>No system is 100% secure. If we ever discover a breach that materially affects you, we will notify you and the appropriate regulators without undue delay.</p>
    </>) },
  { n: 11, id: "children", title: "Children",
    body: <p>CareerOS-AI is not directed to children under 16. We do not knowingly collect data from anyone under 16. If you believe a child has provided data to us, contact us and we will delete it.</p> },
  { n: 12, id: "changes", title: "Changes to this policy",
    body: <p>We may update this Privacy Policy. Material changes will be communicated via the website, in-app notice, or email where required. The &quot;Last Updated&quot; date at the top of this page reflects the current version.</p> },
  { n: 13, id: "contact", title: "Contact us",
    body: (<>
      <p>Questions or requests?</p>
      <p className="not-prose mt-4 rounded-lg border border-border p-4 bg-card/60 font-mono text-sm leading-relaxed">
        CareerOS-AI — Privacy<br />
        Email: <a className="underline" href="mailto:support@careeros-ai.com">support@careeros-ai.com</a><br />
        Website: <a className="underline" href="https://www.careeros-ai.com">https://www.careeros-ai.com</a>
      </p>
    </>) },
];

const INTRO = (
  <>
    <p>This Privacy Policy explains what information CareerOS-AI collects, how we use it, who we share it with, and the rights you have. We aim to be plain-spoken — if any section is unclear, tell us.</p>
    <p>By using CareerOS-AI you consent to the practices described in this policy and to our <Link to="/terms">Terms and Conditions</Link>.</p>
  </>
);

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/privacy"
    />
  );
}
